import type { ResourceServiceType } from "@core-ui/ContextServices/ResourceService/ResourceService";
import { canAddToGallery } from "@ext/markdown/elements/gallery/edit/logic/addToGallery";
import { addImagesToGallery } from "@ext/markdown/elements/gallery/edit/logic/createGallery";
import createImages from "@ext/markdown/elements/image/edit/logic/createImages";
import { NodeSelection } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

const htmlHasTextContent = (html: string) => {
	const { body } = new DOMParser().parseFromString(html, "text/html");
	body.querySelectorAll("img, picture, svg").forEach((node) => node.remove());
	return body.textContent.trim().length > 0;
};

export const clipboardHasRichText = ({ clipboardData }: ClipboardEvent): boolean => {
	const html = clipboardData.getData("text/html");
	return !!clipboardData.getData("text/plain") && !!html && htmlHasTextContent(html);
};

export const getFigureCaption = (html: string): string | undefined => {
	const { body } = new DOMParser().parseFromString(html, "text/html");
	const figure = body.querySelector("figure");
	const caption = figure?.querySelector(":scope > figcaption")?.textContent.trim();
	if (!caption || !figure.querySelector("img")) return undefined;
	return body.textContent.trim() === figure.textContent.trim() ? caption : undefined;
};

const imageHandlePaste = (
	view: EditorView,
	event: ClipboardEvent,
	fileName: string,
	resourceService: ResourceServiceType,
): boolean => {
	if (event.clipboardData.files.length === 0) return false;
	const html = event.clipboardData.getData("text/html");
	const caption = html ? getFigureCaption(html) : undefined;
	if (!caption && clipboardHasRichText(event)) return false;

	const images = Array.from(event.clipboardData.items)
		.filter((item) => item.type.startsWith("image"))
		.map((item) => item.getAsFile())
		.filter(Boolean);
	if (!images.length) return false;

	const { selection, doc } = view.state;
	if (selection instanceof NodeSelection && canAddToGallery(doc, selection.from))
		void addImagesToGallery(images, view, selection.node, fileName, resourceService);
	else void createImages(images, view, fileName, resourceService, caption);
	return true;
};

export default imageHandlePaste;
